import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin, Navigation, ExternalLink, Phone, Loader2, AlertCircle, Search, Filter } from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import { Container, Section, SectionHeading, Breadcrumbs, Card, Button, Badge, EmptyState, Alert, FormField } from '../../components/portal/ui'
import {
  findFacilities,
  listLocations,
  FACILITY_TYPE_LABELS,
  type Facility,
  type FacilityType,
  type FacilityLocations,
} from '../../services/facilityService'
import { TranslatedContentNotice } from '../../components/portal/TranslatedContentNotice'

const TYPES = Object.keys(FACILITY_TYPE_LABELS) as FacilityType[]

export default function FindTBCentrePage() {
  const { t, i18n } = useTranslation()
  const [q, setQ] = useState('')
  const [type, setType] = useState('')
  const [district, setDistrict] = useState('')
  const [taluka, setTaluka] = useState('')
  const [state, setState] = useState('')
  const [results, setResults] = useState<Facility[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searched, setSearched] = useState(false)
  const [geoStatus, setGeoStatus] = useState<string | null>(null)
  const [locations, setLocations] = useState<FacilityLocations | null>(null)

  // Load the state/district/taluka tree once for the filter dropdowns.
  useEffect(() => {
    listLocations().then(setLocations).catch(() => { /* dropdowns just stay empty */ })
  }, [])

  // When only one state exists (e.g. Maharashtra-only directory), auto-select it so
  // the district/taluka dropdowns populate without a hidden state selector.
  useEffect(() => {
    if (locations && !state) {
      const keys = Object.keys(locations)
      if (keys.length === 1) setState(keys[0])
    }
  }, [locations, state])

  const states = locations ? Object.keys(locations) : []
  const districts = state && locations?.[state] ? Object.keys(locations[state]) : []
  const talukas = state && district && locations?.[state]?.[district] ? locations[state][district] : []

  // When the state changes, reset the dependent dropdowns.
  const changeState = (s: string) => { setState(s); setDistrict(''); setTaluka('') }
  const changeDistrict = (d: string) => { setDistrict(d); setTaluka('') }

  const runSearch = async (geo?: { lat: number; lng: number }) => {
    setLoading(true); setError(null); setSearched(true)
    try {
      const data = await findFacilities({
        q: q.trim() || undefined,
        district: district || undefined,
        taluka: taluka || undefined,
        state: state || undefined,
        type: type || undefined,
        lat: geo?.lat,
        lng: geo?.lng,
        radius: 50,
        limit: 30,
      })
      setResults(data)
    } catch (e: any) {
      setError(e?.response?.data?.message || t('pages.findTbCentre.noResultsDesc'))
    } finally { setLoading(false) }
  }

  const search = () => runSearch()

  const useMyLocation = () => {
    if (!navigator.geolocation) { setGeoStatus(t('pages.findTbCentre.geoUnsupported')); return }
    setGeoStatus(t('pages.findTbCentre.geoFinding')); setError(null)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await runSearch({ lat: pos.coords.latitude, lng: pos.coords.longitude })
          setGeoStatus(null)
        } catch (e: any) {
          setGeoStatus(null); setError(e?.response?.data?.message || t('pages.findTbCentre.noResultsDesc'))
        }
      },
      () => setGeoStatus(t('pages.findTbCentre.geoDenied')),
      { timeout: 10000 }
    )
  }

  const clearAll = () => {
    setQ(''); setType(''); setDistrict(''); setTaluka(''); setResults([]); setSearched(false); setError(null); setGeoStatus(null)
  }

  // Auto-load default results on first open so the page shows real, verified
  // facilities immediately — the visitor doesn't have to click Search to see
  // data. A no-filter search returns the top 30 verified facilities.
  useEffect(() => {
    runSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const facilityTypeLabel = (val: FacilityType) =>
    t(`pages.getHelp.facility.facilityTypes.${val}`, { defaultValue: FACILITY_TYPE_LABELS[val] })

  const activeLang = i18n.language?.split('-')[0] || 'en'

  return (
    <PortalLayout>
      <Seo
        title={t('pages.findTbCentre.seo.title')}
        description={t('pages.findTbCentre.seo.description')}
        canonicalPath="/find-tb-centre"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.findTbCentre', 'Find a TB Centre'), path: '/find-tb-centre' }]}
      />

      {/* Hero with the prominent single search box */}
      <Section className="pb-8">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.findTbCentre', 'Find a TB Centre') }]} />
          <SectionHeading as="h1" eyebrow={t('pages.findTbCentre.hero.eyebrow')} title={t('pages.findTbCentre.hero.title')} description={t('pages.findTbCentre.hero.description')} />

          <div className="mt-6 max-w-3xl">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-snow-400" aria-hidden="true"><Search className="w-5 h-5" /></span>
                <input
                  id="tb-centre-q"
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t('pages.findTbCentre.searchPlaceholder')}
                  className="input pl-10 text-base min-h-[44px]"
                  onKeyDown={(e) => e.key === 'Enter' && search()}
                  aria-label={t('pages.findTbCentre.searchPlaceholder')}
                />
              </div>
              <Button onClick={search} variant="primary" size="md" icon={loading ? <Loader2 className="w-5 h-5 animate-spin" /> : undefined} disabled={loading} className="min-h-[44px]">
                {loading ? t('pages.findTbCentre.searching') : t('pages.findTbCentre.searchBtn')}
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <Button onClick={useMyLocation} variant="outline" size="sm" icon={<Navigation className="w-4 h-4" />}>{t('pages.findTbCentre.useLocation')}</Button>
              <a href="tel:1800116666" className="inline-flex items-center gap-2 text-sm font-medium text-[rgb(15_76_129)] hover:underline">
                <Phone className="w-4 h-4" aria-hidden="true" /> {t('pages.findTbCentre.helplineNote')}: 1800-11-6666
              </a>
            </div>
            {geoStatus && (
              <div className="mt-3 text-sm text-snow-600 bg-white/60 p-3 rounded-lg" role="status">{geoStatus}</div>
            )}
          </div>
        </Container>
      </Section>

      {/* Filters */}
      <Section className="bg-white py-6">
        <Container>
          <div className="flex items-center gap-2 mb-3 text-snow-700">
            <Filter className="w-4 h-4" aria-hidden="true" />
            <h2 className="font-semibold text-sm uppercase tracking-wide">{t('pages.findTbCentre.filtersTitle')}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {states.length > 1 && (
              <FormField label={t('pages.getHelp.facility.stateLabel')} htmlFor="fc-state">
                <select id="fc-state" value={state} onChange={(e) => changeState(e.target.value)} className="select">
                  <option value="">{t('pages.getHelp.facility.allTypes')}</option>
                  {states.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </FormField>
            )}
            <FormField label={t('pages.findTbCentre.districtLabel')} htmlFor="fc-district">
              <select id="fc-district" value={district} onChange={(e) => changeDistrict(e.target.value)} className="select">
                <option value="">{t('pages.findTbCentre.allDistricts')}</option>
                {districts.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </FormField>
            <FormField label={t('pages.findTbCentre.talukaLabel')} htmlFor="fc-taluka">
              <select id="fc-taluka" value={taluka} onChange={(e) => setTaluka(e.target.value)} className="select" disabled={!district}>
                <option value="">{t('pages.findTbCentre.allTalukas')}</option>
                {talukas.map((tk) => <option key={tk} value={tk}>{tk}</option>)}
              </select>
            </FormField>
            <FormField label={t('pages.findTbCentre.typeLabel')} htmlFor="fc-type">
              <select id="fc-type" value={type} onChange={(e) => setType(e.target.value)} className="select">
                <option value="">{t('pages.findTbCentre.allTypes')}</option>
                {TYPES.map((t2) => <option key={t2} value={t2}>{facilityTypeLabel(t2)}</option>)}
              </select>
            </FormField>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <Button onClick={search} variant="primary" size="sm" icon={loading ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined} disabled={loading}>
              {loading ? t('pages.findTbCentre.searching') : t('pages.findTbCentre.searchBtn')}
            </Button>
            <Button onClick={clearAll} variant="ghost" size="sm">{t('pages.findTbCentre.clear')}</Button>
          </div>
        </Container>
      </Section>

      {/* Results */}
      <Section className="bg-snow-50 pt-2 pb-12">
        <Container>
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm" role="alert"><AlertCircle className="w-4 h-4" /> {error}</div>
          )}

          {searched && !loading && !error && (
            <>
              <p className="text-sm text-snow-500 mb-3" aria-live="polite">{t('pages.findTbCentre.resultsHeading', { count: results.length })}</p>
              {results.some((f) => f.matchedLanguage && f.matchedLanguage !== activeLang) && (
                <div className="mb-3"><TranslatedContentNotice matchedLanguage="en" /></div>
              )}
              {results.length === 0 ? (
                <EmptyState
                  icon={<MapPin className="w-7 h-7" />}
                  title={t('pages.findTbCentre.noResultsTitle')}
                  description={t('pages.findTbCentre.noResultsDesc')}
                  action={<a href="tel:1800116666" className="btn-primary btn-sm">{t('pages.findTbCentre.callBtn')}</a>}
                />
              ) : (
                <div className="space-y-3">
                  {results.map((f) => (
                    <Card key={f.id} className="p-5 flex flex-col sm:flex-row sm:items-start gap-4">
                      <span className="shrink-0 w-11 h-11 rounded-lg bg-[rgb(240_252_255)] text-[rgb(15_76_129)] flex items-center justify-center" aria-hidden="true"><MapPin className="w-6 h-6" /></span>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-bold text-snow-900 text-lg">{f.name}</h3>
                          <Badge tone="slate">{facilityTypeLabel(f.facilityType)}</Badge>
                          {f.distanceKm != null && <Badge tone="teal">{t('pages.findTbCentre.distanceBadge', { count: f.distanceKm })}</Badge>}
                        </div>
                        {f.address && <p className="text-sm text-snow-600 mt-1.5">{f.address}</p>}
                        <p className="text-xs text-snow-500 mt-1">
                          {[f.taluka && `${t('pages.findTbCentre.talukaLabel')}: ${f.taluka}`, f.district, f.state, f.pincode].filter(Boolean).join(' · ')}
                        </p>
                        {f.phone && (
                          <p className="text-sm text-snow-700 mt-1.5">
                            <Phone className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-[rgb(15_76_129)]" aria-hidden="true" />
                            <a href={`tel:${f.phone}`} className="hover:underline">{f.phone}</a>
                          </p>
                        )}
                        {f.servicesOffered && <p className="text-xs text-snow-500 mt-1.5"><span className="font-medium">{t('pages.findTbCentre.services')}</span> {f.servicesOffered}</p>}
                        {f.operatingHours && <p className="text-xs text-snow-500"><span className="font-medium">{t('pages.findTbCentre.hours')}</span> {f.operatingHours}</p>}
                      </div>
                      <div className="flex sm:flex-col gap-2 shrink-0">
                        {f.latitude != null && f.longitude != null && (
                          <a href={`https://www.google.com/maps/dir/?api=1&destination=${f.latitude},${f.longitude}`} target="_blank" rel="noopener noreferrer" className="btn-primary btn-sm inline-flex items-center gap-1">
                            {t('pages.findTbCentre.directions')} <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                          </a>
                        )}
                        {f.phone && (
                          <a href={`tel:${f.phone}`} className="btn-outline btn-sm inline-flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" aria-hidden="true" /> {t('pages.findTbCentre.call')}
                          </a>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </Container>
      </Section>

      {/* Disclaimer */}
      <Section className="bg-white pt-0 pb-12">
        <Container>
          <Alert tone="info" title={t('pages.findTbCentre.disclaimerTitle')}>
            {t('pages.findTbCentre.disclaimerBody')}
          </Alert>
        </Container>
      </Section>
    </PortalLayout>
  )
}